import type { LiveInfo, LiveStreamRef } from "@tih/shared";
import { hasConfiguredStream, isBroadcastLive } from "./live-display";

export type HomepageVideo = {
  published: boolean;
  youtubeId: string;
  title: string;
  subtitle: string;
  eventId: number | null;
};

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** Pull an 11-character video id from a raw id or common YouTube URL. */
export function extractYoutubeId(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  if (YOUTUBE_ID.test(raw)) return raw;

  try {
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`);
    const host = url.hostname.replace(/^www\./, "").replace(/^m\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0] ?? "";
      return YOUTUBE_ID.test(id) ? id : null;
    }
    if (host === "youtube.com" || host === "music.youtube.com" || host === "youtube-nocookie.com") {
      const fromQuery = url.searchParams.get("v")?.trim() ?? "";
      if (YOUTUBE_ID.test(fromQuery)) return fromQuery;
      const parts = url.pathname.split("/").filter(Boolean);
      if (
        parts[0] === "embed" ||
        parts[0] === "live" ||
        parts[0] === "shorts" ||
        parts[0] === "v"
      ) {
        const id = parts[1] ?? "";
        return YOUTUBE_ID.test(id) ? id : null;
      }
    }
  } catch {
    /* not a URL */
  }
  return null;
}

export function parseHomepageVideo(raw: unknown): HomepageVideo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const youtubeRaw = typeof o.youtubeId === "string" ? o.youtubeId : "";
  const youtubeId = extractYoutubeId(youtubeRaw) ?? youtubeRaw.trim();
  const eventId =
    typeof o.eventId === "number" && Number.isFinite(o.eventId) ? o.eventId : null;
  return {
    published: o.published === true,
    youtubeId,
    title: typeof o.title === "string" ? o.title : "",
    subtitle: typeof o.subtitle === "string" ? o.subtitle : "",
    eventId,
  };
}

export function homepageVideoFromCopy(copy: unknown): HomepageVideo | null {
  if (!copy || typeof copy !== "object" || Array.isArray(copy)) return null;
  const raw = (copy as Record<string, unknown>).homepageVideo;
  if (raw == null) return null;
  return parseHomepageVideo(raw);
}

function replayRef(video: HomepageVideo, prev: LiveStreamRef | null): LiveStreamRef {
  return {
    ...prev,
    youtubeId: video.youtubeId,
    source: { kind: "youtube", src: video.youtubeId, poster: prev?.source?.poster ?? null },
    title: video.title.trim() || prev?.title || "Together In Heaven",
    subtitle: video.subtitle.trim() || prev?.subtitle || null,
  };
}

/**
 * Live broadcasts always win. A published youtubeId replaces `liveInfo.last`
 * so the homepage player shows that replay.
 */
export function applyHomepageVideoToLiveInfo(
  liveInfo: LiveInfo | null,
  video: HomepageVideo | null,
): LiveInfo | null {
  if (isBroadcastLive(liveInfo)) return liveInfo;
  if (!video?.published || !video.youtubeId) return liveInfo;

  const base: LiveInfo = liveInfo ?? {
    isLive: false,
    current: null,
    last: {
      title: "",
      subtitle: null,
      streamedAt: null,
    },
  };

  return {
    ...base,
    last: {
      ...replayRef(video, base.last),
      streamedAt: base.last.streamedAt,
    },
  };
}

/**
 * A live stream always shows. `published: false` hides a configured replay
 * (not a live stream). A published id shows even when `/api/live` has no last.
 */
export function shouldShowHomepageVideo(
  liveInfo: LiveInfo | null,
  video: HomepageVideo | null,
): boolean {
  if (isBroadcastLive(liveInfo)) return true;
  if (video && video.published === false) return false;
  if (video?.published && video.youtubeId) return true;
  return hasConfiguredStream(liveInfo);
}
